import { useEffect, useRef, useState } from "react";
import { CircularProgress, Grid } from "@mui/material";

import Box from "../Box";
import Modal from "../Modal";
import Button from "../Button";
import FormField from "../FormField";
import Typography from "../Typography";
import Autocomplete from "../Autocomplete";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import CustomStatusCell from "../CustomStatusCell";
import GroupService from "../../services/identity/group.service";

import { Mode, Status } from "../../constants/enums";
import { ICodename } from "../../commons/interfaces";
import { SelectMultiLanguage } from "../MultiLanguage";
import { IDataDetailGroup } from "../../containers/group/edit";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";



interface IProps {
    mode?: number;
    lable?: string;
    openModal: boolean;

    organizationId: string;
    groupIdDefault?: string;
    listParentGroup?: ICodename[];
    dataDetailGroup?: IDataDetailGroup;

    hidenMultilanguage?: boolean;
    hidenInputSelectGroupParent?: boolean;
    requiredInputSelectGroupParent?: boolean;

    onCloseModal: () => void;
    onActionModal: (data: IDataDetailGroup) => void;
}

const ModalCreateUpdateGroup = (props: IProps) => {
    const strings = useStrings();

    const closeRef = useRef<any>();
    const submitRef = useRef<any>();
    const [textTitle, setTextTitle] = useState<string>(strings.format(strings.Group.TITLE_CREATE, props.lable?.toLowerCase()));

    return (
        <Modal
            fullWidth
            maxWidth="md"
            title={textTitle}
            visible={props.openModal || false}
            hasActionButton={props.mode !== Mode.View}
            key={`modal_group_${props.dataDetailGroup?.id || ""}`}
            buttonAction={props.dataDetailGroup?.id ? strings.Common.SAVE : strings.Common.CREATE_NEW}

            onClose={() => {
                closeRef.current?.click();
            }}
            onAction={() => {
                submitRef.current?.click();
            }}
        >
            <>
                <CardCreateUpdateGroup
                    {...props}
                    closeRef={closeRef}
                    submitRef={submitRef}
                    setTextTitle={setTextTitle}
                />
            </>
        </Modal>
    );
};

export default ModalCreateUpdateGroup;

export const CardCreateUpdateGroup = (props: IProps & {
    closeRef: React.MutableRefObject<HTMLButtonElement>,
    submitRef: React.MutableRefObject<HTMLButtonElement>,
    setTextTitle: React.Dispatch<React.SetStateAction<string>>,
}) => {
    const strings = useStrings();

    const {
        clientId,
        language,
        listMultiLanguage,
        onError,
        onSuccess,
    } = useCommonComponentContext();

    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [valMultilanguage, setValMultilanguage] = useState<string>(language);
    const [dataDetailGroup, setDataDetailGroup] = useState<IDataDetailGroup | undefined>(undefined);
    const [errorDetailGroup, setErrorDetailGroup] = useState<{ [key in keyof IDataDetailGroup]?: string }>({});

    useEffect(() => { setValMultilanguage(language); }, [language]);

    useEffect(() => {
        setLoadingModal(true);
        if (props.openModal) {
            setErrorDetailGroup({});

            if (Helpers.isNullOrEmpty(props.dataDetailGroup?.id)) {
                props.setTextTitle(strings.format(strings.Group.TITLE_CREATE, props.lable?.toLowerCase()))
            } else {
                const valTitle = props.mode === Mode.View ? strings.Group.TITLE_DETAIL : strings.Group.TITLE_UPDATE;
                props.setTextTitle(strings.format(valTitle, props.lable?.toLowerCase()));

                setDataDetailGroup(props.dataDetailGroup);
            }

            setLoadingModal(false);
        } else {
            props.setTextTitle(strings.format(strings.Group.TITLE_CREATE, props.lable?.toLowerCase()));

            setLoadingModal(false);
        }
    }, [props.openModal, props.dataDetailGroup?.id]);

    const onChangeValue = async (newValue: any, key: string, isMultiLang?: boolean) => {
        setDataDetailGroup(prev => {
            let dataTemp: any = { ...prev };

            if (isMultiLang && isMultiLang === true) {
                dataTemp[key] = {
                    ...dataTemp[key],
                    value: {
                        ...dataTemp[key]?.value,
                        [valMultilanguage]: newValue
                    }
                };
            } else {
                dataTemp[key] = { value: newValue };
            }
            return dataTemp;
        });

        setErrorDetailGroup(prev => ({
            ...prev,
            [key]: undefined,
        }));
    };

    const handleValidateDataGroup = () => {
        let isChecked = true;
        let newErr: any = { ...errorDetailGroup };

        if (Helpers.isNullOrEmpty(dataDetailGroup?.name?.value?.[valMultilanguage])) {
            newErr["name"] = strings.Common.REQUIRED;
            isChecked = false;
        }

        if (props.requiredInputSelectGroupParent
            && Helpers.isNullOrEmpty(dataDetailGroup.parentGroupId)) {
            newErr["parentGroupId"] = { error: strings.Common.REQUIRED };
            isChecked = false;
        }

        if (!isChecked) {
            setErrorDetailGroup(newErr);
        }

        return isChecked;
    };

    const handleCreateUpdateGroup = async () => {
        if (!handleValidateDataGroup()) { }
        else {
            try {
                setLoadingModal(true);

                const parentGroupId = Helpers.isNullOrEmpty(dataDetailGroup?.parentGroupId)
                    ? Helpers.isNullOrEmpty(props.groupIdDefault) ? undefined : props.groupIdDefault
                    : dataDetailGroup?.parentGroupId;

                const dataSubmit = {
                    clientId: clientId,
                    organizationId: props.organizationId,

                    parentGroupId: parentGroupId,

                    name: Helpers.getDataMultiLanguage({
                        data: dataDetailGroup?.name,
                        currentLanguage: valMultilanguage,
                        listLanguage: listMultiLanguage,
                    }) || { value: { [language]: "" } },

                    description: Helpers.getDataMultiLanguage({
                        data: dataDetailGroup?.description,
                        currentLanguage: valMultilanguage,
                        listLanguage: listMultiLanguage,
                    }) || { value: { [language]: "" } },

                    isActive: Status.Active,
                };

                let result: any;
                if (Helpers.isNullOrEmpty(dataDetailGroup?.id)) {
                    result = await GroupService.create(dataSubmit);

                    props.onActionModal({
                        ...dataDetailGroup,
                        id: result?.result?.id || result?.result,
                    });

                    setErrorDetailGroup({});
                    setDataDetailGroup(undefined);
                } else {
                    result = await GroupService.update({
                        ...dataSubmit,
                        id: dataDetailGroup?.id,
                        isActive: dataDetailGroup?.status,
                    });

                    props.onActionModal({ ...dataDetailGroup });

                    setErrorDetailGroup({});
                    setDataDetailGroup(undefined);
                }

                onSuccess(
                    Helpers.isNullOrEmpty(dataDetailGroup?.id)
                        ? strings.format(strings.Group.CREATE_SUCCESS, props.lable)
                        : strings.format(strings.Group.UPDATE_SUCCESS, props.lable)
                );

            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                setLoadingModal(false);
            }
        }
    };

    return (
        <Box marginTop={2}>
            <Box sx={{ display: "none", visibility: "hidden" }}>
                {/* action close */}
                <Button
                    variant="text"
                    color="secondary"
                    ref={props.closeRef}
                    onClick={() => {
                        props.onCloseModal();

                        setErrorDetailGroup({});
                        setDataDetailGroup(undefined);
                    }}
                ></Button>
                {/* action submit */}
                <Button
                    variant="text"
                    color="secondary"
                    ref={props.submitRef}
                    onClick={() => {
                        handleCreateUpdateGroup();
                    }}
                ></Button>
            </Box>
            {loadingModal &&
                <Box sx={{
                    height: "auto",
                    minHeight: "30vh",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }} >
                    <CircularProgress color="info" />
                </Box>
            }

            {!loadingModal &&
                <>
                    <Box sx={{
                        gap: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <Box>
                            {!Helpers.isNullOrEmpty(dataDetailGroup?.id) &&
                                !Helpers.isNullOrEmpty(dataDetailGroup?.status) &&
                                <CustomStatusCell status={dataDetailGroup?.status} />
                            }
                        </Box>
                        {
                            !props.hidenMultilanguage &&
                            (props.mode !== Mode.View) &&
                            <SelectMultiLanguage
                                defaultLanguage={valMultilanguage}
                                onChangeLanguage={(value) => {
                                    setErrorDetailGroup(prev => ({
                                        ...prev,
                                        name: undefined,
                                        description: undefined,
                                    }));

                                    setDataDetailGroup(prev => ({
                                        ...prev,
                                        name: Helpers.getDataMultiLanguage({
                                            data: dataDetailGroup?.name,
                                            currentLanguage: valMultilanguage,
                                            listLanguage: listMultiLanguage,
                                        }),
                                        description: Helpers.getDataMultiLanguage({
                                            data: dataDetailGroup?.description,
                                            currentLanguage: valMultilanguage,
                                            listLanguage: listMultiLanguage,
                                        }),
                                    }));
                                    setValMultilanguage(value);
                                }}
                            />
                        }
                    </Box>
                    <Grid container spacing={2} p={2}>
                        <Grid item xs={12} md={12}>
                            <FormField
                                required
                                maxLength={255}
                                mode={props.mode}
                                errorMessage={errorDetailGroup?.name || ""}
                                defaultValue={dataDetailGroup?.name?.value?.[valMultilanguage]}
                                placeholder={strings.format(strings.Group.ENTER_NAME_GROUP, props.lable)}
                                label={Helpers.capitalizeFirstLetter(strings.format(strings.Group.NAME_GROUP, props.lable))}
                                onBlur={(value) => onChangeValue(value, "name", true)}
                            />
                        </Grid>

                        {!props.hidenInputSelectGroupParent &&
                            <Grid item xs={12} md={12}>
                                <Autocomplete
                                    mode={props.mode}
                                    data={props.listParentGroup || []}
                                    required={props.requiredInputSelectGroupParent}
                                    defaultValue={dataDetailGroup?.parentGroupId}
                                    errorMessage={errorDetailGroup?.parentGroupId || ""}
                                    placeholder={strings.format(strings.Group.SELECT_GROUP_PARENT, props.lable)}
                                    label={Helpers.capitalizeFirstLetter(strings.format(strings.Group.GROUP_PARENT, props.lable))}
                                    onChange={(value) => onChangeValue(value, "parentGroupId")}
                                />
                            </Grid>
                        }

                        <Grid item xs={12} md={12}>
                            <FormField
                                isFullRow
                                multiline
                                maxLength={255}
                                mode={props.mode}
                                label={strings.Organization.DESCRIPTION}
                                placeholder={strings.Organization.ENTER_DESCRIPTION}
                                defaultValue={dataDetailGroup?.description?.value?.[valMultilanguage]}
                                onBlur={(value) => onChangeValue(value, "description", true)}
                            />
                        </Grid>
                    </Grid>
                </>
            }
        </Box>
    );
};
